import useHttp from '../helpers/HttpMethods';
import {
  MessageTemplate,
  MessageTemplateInput,
  PaginatedMessageTemplates
} from '../types/MessageTemplateTypes';

/**
 * Cria um FormData com o template JSON e array vazio de files.
 * @param {MessageTemplateInput} templateData - Dados do modelo de mensagem.
 * @returns {FormData} - FormData preparado para envio.
 */
const createFormData = (templateData: MessageTemplateInput): FormData => {
  const formData = new FormData();

  // Adiciona o template como JSON stringificado em um Blob
  formData.append(
    'template',
    new Blob([JSON.stringify(templateData)], { type: 'application/json' })
  );

  // Adiciona array vazio de files como string JSON vazia
  // O backend espera o campo files mesmo quando vazio
  formData.append('files', new Blob(['[]'], { type: 'application/json' }));

  return formData;
};

class MessageTemplateService {
  /**
   * Lista os modelos de mensagens paginados.
   * @param {string} filter - Filtros em formato de string de query params.
   * @returns {Promise<PaginatedMessageTemplates | Response>} - Modelos de mensagens paginados.
   */
  static async list(
    filter: string
  ): Promise<PaginatedMessageTemplates | Response> {
    return await useHttp().get(`/message-templates?${filter}`);
  }

  /**
   * Busca um modelo de mensagem por ID.
   * @param {number} id - ID do modelo de mensagem.
   * @returns {Promise<MessageTemplate | Response>} - Dados completos do modelo de mensagem.
   */
  static async findById(id: number): Promise<MessageTemplate | Response> {
    return await useHttp().get(`/message-templates/${id}`);
  }

  /**
   * Cria um novo modelo de mensagem.
   * @param {MessageTemplateInput} body - Dados do modelo de mensagem.
   * @returns {Promise<MessageTemplate | Response>} - Modelo de mensagem criado ou resposta de erro.
   */
  static async create(
    body: MessageTemplateInput
  ): Promise<MessageTemplate | Response> {
    const formData = createFormData(body);
    return await useHttp().postFormdata(formData, '/message-templates');
  }

  /**
   * Atualiza um modelo de mensagem existente.
   * @param {number} id - ID do modelo de mensagem a ser atualizado.
   * @param {MessageTemplateInput} body - Dados do modelo de mensagem para atualização.
   * @returns {Promise<MessageTemplate | Response>} - Modelo de mensagem atualizado ou resposta de erro.
   */
  static async update(
    id: number,
    body: MessageTemplateInput
  ): Promise<MessageTemplate | Response> {
    const formData = createFormData(body);
    return await useHttp().putFile(formData, `/message-templates/${id}`);
  }

  /**
   * Remove um modelo de mensagem.
   * @param {number} id - ID do modelo de mensagem.
   * @returns {Promise<Response>} - Retorno padrão de uma request.
   */
  static async delete(id: number): Promise<Response> {
    return await useHttp().del(`/message-templates/${id}`);
  }

  /**
   * Aplica um modelo de mensagem substituindo as variáveis pelos valores reais.
   * @param {number} templateId - ID do modelo de mensagem.
   * @param {number} ticketId - ID do ticket (opcional, apenas se o ticket já estiver salvo).
   * @returns {Promise<{ templateId: number; title: string; content: string; evaluatedResponse: string; files: any[] } | Response>} - Resposta do modelo aplicado.
   */
  static async apply(
    templateId: number,
    ticketId?: number
  ): Promise<
    | {
        templateId: number;
        title: string;
        content: string;
        evaluatedResponse: string;
        files: any[];
      }
    | Response
  > {
    const params = new URLSearchParams();
    if (ticketId) {
      params.append('ticketId', ticketId.toString());
    }
    const queryString = params.toString();
    const url = `/message-templates/${templateId}/apply${
      queryString ? `?${queryString}` : ''
    }`;
    return await useHttp().get(url);
  }
}

export default MessageTemplateService;
